import { MouseEventHandler, ReactNode } from "react"
import { IconButton } from "@mui/material"
import { InfoOutlined, Refresh, Warning } from "@mui/icons-material"
import OPTooltip from "components/material/tooltip"
import { IntradayRelativeDay } from "helpers/intraday"

export default function IntradayDataInfo({
  isIntradayEnabled,
  isEndDateToday,
  pricingAsOf,
  batchAsOf,
  intradayRelativeDay = "today",
  showWarning,
  showRefreshButton,
  onRefresh,
  refreshButtonTooltip,
  isLoadingDates,
  useLatestDate,
}: IntradayDataInfoProps) {
  if (!isIntradayEnabled || isLoadingDates) return null

  if (showWarning) {
    return (
      <OPTooltip title={<span>
        No intraday data
        {useLatestDate && <>
          <br />
          Using end of previous day
        </>}
      </span>}
      >
        <Warning className="intraday-warning" />
      </OPTooltip>
    )
  }

  const asOf = pricingAsOf ?? batchAsOf

  let timeStamp: ReactNode = null
  if (isEndDateToday && asOf) {
    const batchLabel = batchAsOf
      ? `Batch as of ${intradayRelativeDay !== "today" ? `${intradayRelativeDay} ` : ""}${batchAsOf}`
      : null
    const tooltipTitle = batchLabel ? (
      <span>
        15-minute delayed
        <br />
        {batchLabel}
      </span>
    ) : "15-minute delayed"
    // When falling back to the batch time (no live snapshot yet), keep the prior-day
    // qualifier visible in the label so a stale batch doesn't read as current.
    const asOfLabel = !pricingAsOf && intradayRelativeDay !== "today" ? `${intradayRelativeDay} ${asOf}` : asOf
    timeStamp = (
      <div className="latest-price-info">
        <div className="date-label">
          As Of
          <OPTooltip title={tooltipTitle}>
            <InfoOutlined className="latest-price-info-icon" />
          </OPTooltip>
        </div>
        <span>{asOfLabel}</span>
      </div>
    )
  }

  if (showRefreshButton) {
    return (
      <div className="intraday-data-info">
        {timeStamp}
        <OPTooltip title={refreshButtonTooltip}>
          <IconButton
            color="primary"
            className="intraday-reload-button"
            size="large"
            onClick={onRefresh}
          >
            <Refresh />
          </IconButton>
        </OPTooltip>
      </div>
    )
  }

  return timeStamp
}

interface IntradayDataInfoProps {
  isIntradayEnabled: boolean
  isEndDateToday: boolean
  pricingAsOf: string | null
  batchAsOf: string | null
  intradayRelativeDay?: IntradayRelativeDay
  showWarning: boolean
  showRefreshButton: boolean
  onRefresh: MouseEventHandler<HTMLButtonElement>
  refreshButtonTooltip: string
  isLoadingDates: boolean
  useLatestDate: boolean
}
